import api from '../lib/api';

// Room Type Interfaces
export interface RoomType {
  id: number;
  hotel_id: number;
  room_type_name: string;
  room_description?: string;
  room_size_sqft?: number;
  bed_type: 'King' | 'Queen' | 'Twin' | 'Double' | 'Single' | 'Sofa Bed';
  number_of_beds: number;
  max_adults: number;
  max_children: number;
  max_total_occupancy: number;
  extra_bed_available: boolean;
  max_extra_beds: number;
  extra_bed_charge: number;
  smoking_preference: 'Smoking' | 'Non-Smoking' | 'Both';
  total_rooms: number;
  rooms_allocated_to_trp: number;
  status: 'active' | 'inactive' | 'maintenance';
  display_order: number;
  created_at: string;
  updated_at: string;
}

// Room Plan Interfaces
export interface RoomPlan {
  id: number;
  room_type_id: number;
  plan_name: string;
  plan_code: string;
  occupancy_type: 'Single' | 'Double' | 'Triple' | 'Quad';
  adults_included: number;
  children_included: number;
  meal_plan: 'Room Only' | 'Breakfast' | 'Half Board' | 'Full Board' | 'All Inclusive';
  meal_plan_description?: string;
  is_refundable: boolean;
  refund_policy_text?: string;
  base_rate: number;
  extra_child_charge: number;
  extra_adult_charge: number;
  inclusions?: string;
  exclusions?: string;
  special_conditions?: string;
  status: 'active' | 'inactive' | 'seasonal';
  display_order: number;
  is_popular: boolean;
  created_at: string;
  updated_at: string;
}

// Rate Slab Interfaces
export interface RateSlab {
  id: number;
  room_plan_id: number;
  valid_from: string;
  valid_to: string;
  rate_per_night: number;
  /** Rate for unchecked weekdays; null/omitted = same as rate_per_night */
  weekend_rate_per_night?: number | null;
  /** Bit i (0=Mon..6=Sun): 1 = weekday rate applies */
  weekday_day_mask?: number;
  slab_name?: string;
  slab_description?: string;
  status: 'active' | 'inactive' | 'draft' | 'deleted';
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
}

// Filter Interfaces
export interface RoomTypeFilters {
  status?: string;
  bed_type?: string;
  max_adults?: number;
  search?: string;
}

export interface RoomPlanFilters {
  status?: string;
  occupancy_type?: string;
  meal_plan?: string;
  is_refundable?: boolean;
}

export interface RateSlabFilters {
  status?: string;
  valid_from?: string;
  valid_to?: string;
}

// Response Interfaces
export interface RoomTypeResponse {
  success: boolean;
  data: RoomType[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  timestamp: string;
}

export interface RoomPlanResponse {
  success: boolean;
  data: RoomPlan[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  timestamp: string;
}

export interface RateSlabResponse {
  success: boolean;
  data: RateSlab[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
  timestamp: string;
}

export interface RoomTypeStats {
  total_room_types: number;
  active_room_types: number;
  inactive_room_types: number;
  maintenance_room_types: number;
  total_rooms: number;
  total_trp_rooms: number;
}

class RoomService {
  // Room Types
  async getRoomTypes(hotelId: number, page: number = 1, limit: number = 10, filters?: RoomTypeFilters): Promise<RoomTypeResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    params.append('hotel_id', hotelId.toString());
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.bed_type) params.append('bed_type', filters.bed_type);
    if (filters?.max_adults) params.append('max_adults', filters.max_adults.toString());
    if (filters?.search) params.append('search', filters.search);

    const response = await api.get(`/rooms?${params.toString()}`);
    return response.data;
  }

  async getRoomTypeById(id: number): Promise<{ success: boolean; data: RoomType & { plans: RoomPlan[] } }> {
    const response = await api.get(`/rooms/${id}`);
    return response.data;
  }

  async createRoomType(roomData: Partial<RoomType>): Promise<{ success: boolean; data: RoomType }> {
    const response = await api.post('/rooms', roomData);
    return response.data;
  }

  async updateRoomType(id: number, roomData: Partial<RoomType>): Promise<{ success: boolean; data: RoomType }> {
    const response = await api.put(`/rooms/${id}`, roomData);
    return response.data;
  }

  async deleteRoomType(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/rooms/${id}`);
    return response.data;
  }

  async toggleRoomTypeStatus(id: number, status: 'active' | 'inactive' | 'maintenance'): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/rooms/${id}/status`, { status });
    return response.data;
  }

  async getRoomTypeStats(hotelId?: number): Promise<{ success: boolean; data: RoomTypeStats }> {
    const params = hotelId ? `?hotel_id=${hotelId}` : '';
    const response = await api.get(`/rooms/stats${params}`);
    return response.data;
  }

  // Room Plans
  async getRoomPlans(roomTypeId: number, page: number = 1, limit: number = 10, filters?: RoomPlanFilters): Promise<RoomPlanResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.occupancy_type) params.append('occupancy_type', filters.occupancy_type);
    if (filters?.meal_plan) params.append('meal_plan', filters.meal_plan);
    if (filters?.is_refundable !== undefined) params.append('is_refundable', filters.is_refundable.toString());

    const response = await api.get(`/room-plans?room_type_id=${roomTypeId}&${params.toString()}`);
    return response.data;
  }

  async getRoomPlanById(id: number): Promise<{ success: boolean; data: RoomPlan }> {
    const response = await api.get(`/room-plans/${id}`);
    return response.data;
  }

  async createRoomPlan(planData: Partial<RoomPlan>): Promise<{ success: boolean; data: RoomPlan }> {
    const response = await api.post('/room-plans', planData);
    return response.data;
  }

  async updateRoomPlan(id: number, planData: Partial<RoomPlan>): Promise<{ success: boolean; data: RoomPlan }> {
    const response = await api.put(`/room-plans/${id}`, planData);
    return response.data;
  }

  async deleteRoomPlan(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/room-plans/${id}`);
    return response.data;
  }

  async toggleRoomPlanStatus(id: number, status: 'active' | 'inactive' | 'seasonal'): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/room-plans/${id}/status`, { status });
    return response.data;
  }

  // Rate Slabs
  async getRateSlabs(roomPlanId: number, page: number = 1, limit: number = 10, filters?: RateSlabFilters): Promise<RateSlabResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.valid_from) params.append('valid_from', filters.valid_from);
    if (filters?.valid_to) params.append('valid_to', filters.valid_to);

    const response = await api.get(`/rate-slabs?room_plan_id=${roomPlanId}&${params.toString()}`);
    return response.data;
  }

  async getRateSlabById(id: number): Promise<{ success: boolean; data: RateSlab }> {
    const response = await api.get(`/rate-slabs/${id}`);
    return response.data;
  }

  async createRateSlab(slabData: Partial<RateSlab>): Promise<{ success: boolean; data: RateSlab }> {
    const response = await api.post('/rate-slabs', slabData);
    return response.data;
  }

  async updateRateSlab(id: number, slabData: Partial<RateSlab>): Promise<{ success: boolean; data: RateSlab }> {
    const response = await api.put(`/rate-slabs/${id}`, slabData);
    return response.data;
  }

  async deleteRateSlab(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/rate-slabs/${id}`);
    return response.data;
  }

  async toggleRateSlabStatus(id: number, status: 'active' | 'inactive' | 'draft' | 'deleted'): Promise<{ success: boolean; message: string }> {
    const response = await api.put(`/rate-slabs/${id}/status`, { status });
    return response.data;
  }
}

export default new RoomService();

